import { Component, computed, inject, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import {
  PricingModel,
  ParameterSchema,
  formatParameterChoiceLabel,
} from '../../models/pricing-model.model';
import { ParameterEditorComponent } from '../shared/parameter-editor/parameter-editor.component';
import {
  buildSchemaBackedParameterValues,
  sanitizeParameterValuesForSave,
} from '../shared/parameter-editor/parameter-editor.utils';
import { ChipModule } from 'primeng/chip';
import { combineLatest, filter, map, startWith } from 'rxjs';
import { isPermissionError, parseApiError } from '../quotes-api-error';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { Button } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { FloatLabelModule } from 'primeng/floatlabel';
import { Divider } from 'primeng/divider';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { PricingModelService } from '../services/pricing-model.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { SharedConfirmDialogComponent } from '@tangible/shared';

type ParameterDisplay = {
  name: string;
  label: string;
  value: string | null;
  isSet: boolean;
  schema: ParameterSchema;
};

@Component({
  selector: 'app-pricing-page',
  imports: [
    AsyncPipe,
    ChipModule,
    ReactiveFormsModule,
    CheckboxModule,
    Button,
    Drawer,
    InputTextModule,
    TextareaModule,
    FloatLabelModule,
    Divider,
    Select,
    ParameterEditorComponent,
    Tooltip,
    MultiSelectModule,
    IconFieldModule,
    InputIconModule,
    SharedConfirmDialogComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './pricing-page.component.html',
  styleUrl: './pricing-page.component.scss',
})
export class PricingPageComponent implements OnInit {
  pricingModelService = inject(PricingModelService);
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);
  includeInactive = new FormControl<boolean>(false);
  isDrawerVisible = false;
  pricingModelInDraw: PricingModel | null = null;
  isCreateMode = false;
  activeParameterSchemas: Record<string, ParameterSchema> = {};
  editableParameterValues: Record<string, string | null> = {};
  parametersDirty = false;
  expandedGroups = new Set<string>();

  // Search and filter state
  searchQuery = new FormControl<string>('');
  selectedFunctionKeyFilters = new FormControl<string[]>([]);

  ngOnInit() {
    // Since this page is focused on pricing models, we want it to have the latest data from the server when opened.
    this.pricingModelService.pricingModels.reload();
  }

  pricingModelsByFunctionKey = computed<Map<string, PricingModel>>(() => {
    if (this.pricingModelService.pricingModels.hasValue()) {
      return this.pricingModelService.pricingModels
        .value()
        .reduce((lookup, model) => {
          if (!lookup.has(model.function_key)) {
            lookup.set(model.function_key, model);
          }
          return lookup;
        }, new Map<string, PricingModel>());
    }
    return new Map<string, PricingModel>();
  });

  functionKeyFilterOptions = computed(() => {
    if (this.pricingModelService.pricingModels.hasValue()) {
      const models = this.pricingModelService.pricingModels.value();
      return Array.from(new Set(models.map((model) => model.function_key)))
        .sort()
        .map((functionKey) => ({
          label: functionKey,
          value: functionKey,
        }));
    }
    return [];
  });

  pricingModels$ = combineLatest([
    this.includeInactive.valueChanges.pipe(
      startWith(this.includeInactive.value),
    ),
    this.searchQuery.valueChanges.pipe(startWith(this.searchQuery.value)),
    this.selectedFunctionKeyFilters.valueChanges.pipe(
      startWith(this.selectedFunctionKeyFilters.value),
    ),
    toObservable(this.pricingModelService.pricingModels.value).pipe(
      filter((models) => models !== undefined),
    ),
  ]).pipe(
    map(([includeInactive, search, functionKeys, allModels]) => {
      let models = includeInactive
        ? allModels
        : allModels.filter((model) => model.is_active);

      // Search filter
      const q = (search ?? '').trim().toLowerCase();
      if (q) {
        models = models.filter(
          (m) =>
            m.name.toLowerCase().includes(q) ||
            m.function_key.toLowerCase().includes(q) ||
            (m.description ?? '').toLowerCase().includes(q),
        );
      }

      // Function key filter
      if (functionKeys && functionKeys.length > 0) {
        models = models.filter((m) => functionKeys.includes(m.function_key));
      }

      return [...models].sort((a, b) => {
        const functionKeyCompare = a.function_key.localeCompare(b.function_key);
        return functionKeyCompare || a.name.localeCompare(b.name);
      });
    }),
  );

  get hasActiveFilters(): boolean {
    const fk = this.selectedFunctionKeyFilters.value;
    return (
      (this.searchQuery.value ?? '').trim().length > 0 ||
      (fk !== null && fk.length > 0)
    );
  }

  clearFilters(): void {
    this.searchQuery.setValue('');
    this.selectedFunctionKeyFilters.setValue([]);
  }

  pricingModelFormGroup = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    description: new FormControl(''),
    function_key: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
  });

  get functionKeyControl(): FormControl<string> {
    return this.pricingModelFormGroup.get(
      'function_key',
    ) as FormControl<string>;
  }

  toggleGroup(key: string) {
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      this.expandedGroups.add(key);
    }
  }

  isGroupExpanded(key: string) {
    return this.expandedGroups.has(key);
  }

  getModelsForFunctionKey(
    models: PricingModel[],
    functionKey: string,
  ): PricingModel[] {
    return models.filter((m) => m.function_key === functionKey);
  }

  getFunctionKeysFromModels(models: PricingModel[]): string[] {
    return Array.from(new Set(models.map((m) => m.function_key)));
  }

  toggleAllGroups(keys: string[]): void {
    const allExpanded = keys.every((k) => this.expandedGroups.has(k));
    if (allExpanded) {
      this.expandedGroups.clear();
    } else {
      this.expandedGroups = new Set(keys);
    }
  }

  get hasActiveParameterSchemas(): boolean {
    return Object.keys(this.activeParameterSchemas).length > 0;
  }

  onParameterEditorChange(event: { key: string; value: string | null }): void {
    this.editableParameterValues = {
      ...this.editableParameterValues,
      [event.key]: event.value,
    };
    this.parametersDirty = true;
  }

  getAllParametersForDisplay(model: PricingModel): ParameterDisplay[] {
    const params = model.parameters || {};
    const schemas = model.parameter_schemas || {};
    return Object.entries(schemas).map(([name, schema]) => ({
      name,
      label: schema.label || name,
      value: params[name],
      isSet: params[name] != null,
      schema,
    }));
  }

  /** Display value for tables/chips: use backend label when available, otherwise raw value */
  getParamValueDisplay(param: ParameterDisplay): string {
    if (param.value == null || param.value === '') return '—';
    const choice = param.schema.choices?.find((c) => c.value === param.value);
    return choice ? formatParameterChoiceLabel(choice) : String(param.value);
  }

  getUnsetParamDisplay(param: ParameterDisplay): string {
    if (param.schema.resolved_default == null) {
      return 'Unset';
    }
    const choice = param.schema.choices?.find(
      (c) => c.value === String(param.schema.resolved_default),
    );
    return choice
      ? formatParameterChoiceLabel(choice)
      : String(param.schema.resolved_default);
  }

  get drawerHeader(): string {
    return this.isCreateMode ? 'Create Pricing Model' : 'Edit Pricing Model';
  }

  openConfirm(
    event: Event,
    pricingModel: PricingModel,
    action: 'archive' | 'unarchive',
  ): void {
    const isArchive = action === 'archive';

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Are you sure that you want to ${action} ${pricingModel.name}?`,
      header: `${isArchive ? 'Archive' : 'Unarchive'} ${pricingModel.name}`,
      closable: true,
      closeOnEscape: true,
      icon: isArchive ? 'pi pi-exclamation-triangle' : 'pi pi-refresh',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: isArchive ? 'Archive' : 'Unarchive',
      },
      accept: () => {
        void this.handlePricingModelAction(action, pricingModel);
      },
    });
  }

  private async handlePricingModelAction(
    action: 'archive' | 'unarchive',
    pricingModel: PricingModel,
  ): Promise<void> {
    try {
      await this.pricingModelService.patchPricingModel(pricingModel.id, {
        is_active: action !== 'archive',
      });
    } catch (error: unknown) {
      if (isPermissionError(error)) return;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: parseApiError(
          error,
          `An error occurred while trying to ${action} the pricing model.`,
        ),
      });
    }
  }

  openDrawer(model: PricingModel) {
    if (!model) return;

    this.isCreateMode = false;
    this.pricingModelInDraw = model;
    this.activeParameterSchemas = model.parameter_schemas || {};
    this.editableParameterValues = buildSchemaBackedParameterValues(
      this.activeParameterSchemas,
      model.parameters || {},
    );
    this.parametersDirty = false;
    this.functionKeyControl.disable({ emitEvent: false });
    this.pricingModelFormGroup.patchValue({
      name: model.name,
      description: model.description || '',
      function_key: model.function_key,
    });
    this.pricingModelFormGroup.markAsPristine();
    this.isDrawerVisible = true;
  }

  openCreateDrawer() {
    this.isCreateMode = true;
    this.pricingModelInDraw = null;
    this.activeParameterSchemas = {};
    this.editableParameterValues = {};
    this.parametersDirty = false;
    this.functionKeyControl.enable({ emitEvent: false });
    this.pricingModelFormGroup.reset({
      name: '',
      description: '',
      function_key: '',
    });
    this.isDrawerVisible = true;
  }

  onFunctionKeyChange(functionKey: string | null) {
    const modelForFunctionKey = functionKey
      ? this.pricingModelsByFunctionKey().get(functionKey)
      : null;

    this.activeParameterSchemas = modelForFunctionKey?.parameter_schemas || {};
    this.editableParameterValues = buildSchemaBackedParameterValues(
      this.activeParameterSchemas,
      {},
    );
    this.parametersDirty = false;
  }

  closeDrawer() {
    this.isDrawerVisible = false;
    this.pricingModelInDraw = null;
    this.isCreateMode = false;
    this.activeParameterSchemas = {};
    this.editableParameterValues = {};
    this.parametersDirty = false;
    this.functionKeyControl.enable({ emitEvent: false });
    this.pricingModelFormGroup.reset();
  }

  async savePricingModel() {
    if (this.pricingModelFormGroup.invalid) return;

    const formValue = this.pricingModelFormGroup.getRawValue();
    const parameters = sanitizeParameterValuesForSave(
      this.editableParameterValues,
    );

    try {
      const payload = {
        name: formValue.name || '',
        description: formValue.description || '',
        parameters,
      };

      if (this.isCreateMode) {
        await this.pricingModelService.createPricingModel({
          ...payload,
          function_key: formValue.function_key,
          is_active: true,
        });
      } else if (this.pricingModelInDraw) {
        await this.pricingModelService.patchPricingModel(
          this.pricingModelInDraw.id,
          payload,
        );
      } else {
        return;
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: this.isCreateMode
          ? 'Pricing model created successfully'
          : 'Pricing model updated successfully',
      });
      this.closeDrawer();
    } catch (error: unknown) {
      console.error('Error saving pricing model:', error);
      if (isPermissionError(error)) return;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: parseApiError(
          error,
          'An error occurred while saving the pricing model.',
        ),
      });
      throw error;
    }
  }
}
